import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { useUnifiedOnboarding } from '../../hooks/useUnifiedOnboarding';
import { useAuth } from '../../contexts/AuthContext';
import ProgressBar from '../ui/ProgressBar';
import { UnifiedOnboardingData, UnifiedOnboardingStep } from '../../lib/onboarding/unifiedTypes';
import {createStripeSubscription} from "../../lib/api/stripe";

// Step Components
import AccountCreationStep from './steps/AccountCreationStep';
import CoachPersonalityStep from './steps/CoachPersonalityStep';
import WelcomePaymentStep from './steps/WelcomePaymentStep';
import TimingStep from './steps/TimingStep';
import IntensityStep from './steps/IntensityStep';
import PreferencesStep from './steps/PreferencesStep';
import CompletionStep from './steps/CompletionStep';

const TOTAL_STEPS = 7;

const STEP_COMPONENTS = [
  AccountCreationStep,
  CoachPersonalityStep,
  WelcomePaymentStep,
  TimingStep,
  IntensityStep,
  PreferencesStep,
  CompletionStep,
];

const STEP_TITLES = [
  'Create Your Account',
  'Choose Your Coach Personality',
  'Welcome to OneWin',
  'When Should We Message You?',
  'Coaching Intensity',
  'Additional Preferences',
  'You\'re All Set!',
];

interface UnifiedOnboardingFlowProps {
  onComplete: () => void;
  onClose: () => void;
  initialData?: Partial<UnifiedOnboardingData>;
}

export default function UnifiedOnboardingFlow({ onComplete, onClose, initialData }: UnifiedOnboardingFlowProps) {
  const { signUp, updateUserPreferences, user, refreshUserData, onboardUser } = useAuth();
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [accountCreationError, setAccountCreationError] = useState<string | null>(null);
  const stepContentRef = useRef<HTMLDivElement>(null);

  const {
    data,
    currentStep,
    updateData,
    canGoNext,
    goNext,
    goBack,
    goToStep,
    clearStorage,
    isJudge,
    skipNext
  } = useUnifiedOnboarding();

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const step = event.state?.step;
      if (typeof step === 'number' && step >= 0 && step < TOTAL_STEPS) {
        goToStep(step as UnifiedOnboardingStep);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [goToStep]);

  // Update browser history when step changes
  useEffect(() => {
    if (currentStep === 0 && !data.phoneNumber && initialData?.phoneNumber) {
      updateData({ phoneNumber: initialData.phoneNumber });
    }
    const url = new URL(window.location.href);
    url.searchParams.set('step', currentStep.toString());
    window.history.pushState({ step: currentStep }, '', url.toString());
    stepContentRef.current?.scrollTo({ top: 0, behavior: "instant" });
  }, [currentStep]);

  const handleNext = async () => {
    if (currentStep < TOTAL_STEPS - 1 && canGoNext()) {
      if (currentStep === 1 && isJudge()) {
        skipNext();
      } else {
        goNext();
      }
    } else if (currentStep === TOTAL_STEPS - 1) {
      // Handle account creation on the final step
      await handleAccountCreation();
    }
  };

  const handleAccountCreation = async () => {
    if (!data.email || !data.password) {
      setAccountCreationError('Email and password are required');
      return;
    }

    setIsCreatingAccount(true);
    setAccountCreationError(null);

    try {
      // Map onboarding data to AuthContext format
      const phoneNumber = "+1" + data.phoneNumber.replace(/[^0-9]/g, '');
      const userData = {
        phoneNumber,
        fullName: data.fullName,
        timezone: data.timezone,
        morningTime: data.morningTime,
        eveningTime: data.eveningTime,
        weekendMessaging: true,
        responseMethod: data.communicationPreference === 'text-only' ? 'text' : 'both' as 'text' | 'both',
        goalVisibility: 'private' as const,
        hasAccountabilityPartner: false,
        partnerEmail: '',
        partnerName: '',
        hasPermission: false,
      };

      // Create the user account
      const { data: signUpData, error: signUpError } = await signUp(data.email, data.password, userData);

      if (signUpData?.user?.id && !isJudge()) {
        const {error: subscriptionError} = await createStripeSubscription(data.priceId, data.customerId, data.paymentMethodId, signUpData.user.id);

        if (subscriptionError) {
          throw new Error(subscriptionError);
        }
      }

      if (signUpError) {
        throw new Error(signUpError.message || 'Failed to create account');
      }

      // Manually refresh user data to ensure we have the latest data
      console.log('[UnifiedOnboardingFlow] Refreshing user data after signup...');
      await refreshUserData();

      // Additional delay to ensure all database operations are complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update user preferences with additional onboarding data
      const preferencesUpdate = {
        coach_personality: data.selectedPersonality,
        check_in_style: data.checkInStyle,
        intensity_level: data.intensityLevel,
        additional_preferences: data.additionalPreferences,
      };

      // Get the user ID from the current session or use the one from signup
      const userId = user?.id || signUpData?.user?.id;

      if (userId) {
        console.log('[UnifiedOnboardingFlow] Updating preferences for user:', userId);
        await onboardUser(userId, phoneNumber);
        const { error: preferencesError } = await updateUserPreferences(preferencesUpdate, userId);

        if (preferencesError) {
          console.error('[UnifiedOnboardingFlow] Error updating preferences:', preferencesError);
          // Don't fail the onboarding for preference update errors
        } else {
          console.log('[UnifiedOnboardingFlow] Preferences updated successfully');
        }
      } else {
        console.warn('[UnifiedOnboardingFlow] No user ID available for updating preferences');
      }

      // Clear onboarding data from localStorage
      clearStorage();

      // Complete the onboarding
      onComplete();
    } catch (error) {
      console.error('Account creation error:', error);
      setAccountCreationError('An unexpected error occurred. Please try again.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      goBack();
    }
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOTAL_STEPS - 1;

  const CurrentStepComponent = STEP_COMPONENTS[currentStep];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-1 md:p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[98vh] md:max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-4">
            {!isFirstStep && (
              <button
                onClick={handleBack}
                disabled={isCreatingAccount}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pink-400 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Go back to previous step"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <div>
              <h2 className="md:text-xl font-semibold text-gray-900">
                {STEP_TITLES[currentStep]}
              </h2>
              <p className="text-sm text-gray-500">
                Complete your OneWin setup
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isCreatingAccount}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close onboarding"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4">
          <ProgressBar
            currentStep={currentStep + 1}
            totalSteps={TOTAL_STEPS}
            className="mb-2"
          />
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Step {currentStep + 1} of {TOTAL_STEPS}</span>
            <span>{Math.round(((currentStep + 1) / TOTAL_STEPS) * 100)}% complete</span>
          </div>
        </div>

        {/* Error Message */}
        {accountCreationError && (
          <div className="px-6 py-3 bg-red-50 border-l-4 border-red-400">
            <p className="text-red-700 text-sm">{accountCreationError}</p>
          </div>
        )}

        {/* Step Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] md:max-h-[50vh]" ref={stepContentRef}>
          <CurrentStepComponent
            data={data}
            onUpdate={updateData}
            onNext={handleNext}
          />
        </div>

        {/* Footer Navigation */}
        <div className="p-6 border-t border-gray-100 flex justify-between">
          <div>
            {!isFirstStep && (
              <button
                onClick={handleBack}
                disabled={isCreatingAccount}
                className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
            )}
          </div>

          <button
            onClick={handleNext}
            disabled={!canGoNext() || isCreatingAccount}
            className="px-6 py-3 bg-pink-500 text-white rounded-xl hover:bg-pink-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
          >
            {isCreatingAccount ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating Account...</span>
              </>
            ) : (
              <>
                <span>{isLastStep ? 'Complete Setup' : 'Continue'}</span>
                {!isLastStep && <ArrowRight className="h-4 w-4" />}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
